import { Info } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

type EngagementsBannerProps = {
  total: number;
  growthLabel: string;
  isGrowthPositive: boolean;
  previousPeriodTotal: number;
  uniqueInitiators?: number;
  uniqueTargets?: number;
  className?: string;
};

const ENGAGEMENT_TOOLTIP =
  'Engagements include: Consultation Requests, Calendly Bookings, ' +
  'First Messages, FA Provider Referrals, SP Provider Referrals, ' +
  'and Network Connections.';

export const EngagementsBanner = ({
  total,
  growthLabel,
  isGrowthPositive,
  previousPeriodTotal,
  uniqueInitiators,
  uniqueTargets,
  className,
}: EngagementsBannerProps) => {
  return (
    <div
      className={cn(
        'bg-gradient-to-br from-[#3d6560] to-[#5a8b84] text-white rounded-lg p-6',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white text-lg font-semibold mb-2 flex items-center gap-2">
            Total Engagements Across All Service Providers
            <span className="relative group">
              <Info className="w-4 h-4 text-white/70 cursor-help" />
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10">
                {ENGAGEMENT_TOOLTIP}
              </span>
            </span>
          </h3>
          <p className="text-white/80 text-sm">
            Platform-wide engagement activity
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span
              data-testid="growth-indicator"
              className="text-sm font-medium"
            >
              {isGrowthPositive ? '↑' : '↓'} {growthLabel}
            </span>
            <span className="text-white/70 text-sm">
              vs {previousPeriodTotal.toLocaleString('en-US')} prev. period
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-bold">{total.toLocaleString('en-US')}</div>
          <div className="text-white/80 mt-1 text-sm">
            Cumulative engagements
          </div>
        </div>
      </div>
      {(uniqueInitiators != null || uniqueTargets != null) && (
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/20">
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm">Unique Initiators:</span>
            <span className="text-white font-semibold">
              {(uniqueInitiators ?? 0).toLocaleString('en-US')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm">Unique Targets:</span>
            <span className="text-white font-semibold">
              {(uniqueTargets ?? 0).toLocaleString('en-US')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
